// File (DESIGN §3): the engine's folder tree (`file.tree`) — phases 00–09, sub-folders, program folders with Evidence/ and
// Tasks/ — each with the engine's counts and a progress meter (reviewed of workpapers, as the engine counted them). The right
// pane lists the folder's items: workpapers with sign-off circles, stale badge and open notes, and sub-folders. A rendered
// workpaper opens through a five-minute signed URL from the private bucket (`wp_files`); when there is none the pane says why.
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { Button, Card, ErrorState, LoadState, Meter, SignoffCircles, StatusPill, Tree, type TreeNode } from "../../components";
import type { FileNode, Workpaper, WpFile } from "../../data/types";
import { fmtStamp, plural, titleCase } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { useEngagement } from "./Layout";
import { Act, useAct } from "./shared";

function Badge({ n }: { n: FileNode }) {
  if (!n.exists) return <StatusPill state="missing" label="Missing" />;
  if (n.problems) return <StatusPill state="missing" label={`${n.problems} problem${n.problems === 1 ? "" : "s"}`} />;
  if (n.totals.stale) return <StatusPill state="stale" label={`${n.totals.stale} stale`} />;
  if (n.totals.workpapers) return <Meter value={n.totals.reviewed} max={n.totals.workpapers} label={`${n.label}: workpapers reviewed`} />;
  return null;
}

export function FilePage() {
  const { id, model } = useEngagement();
  const { ds } = useApp();
  const { open, drawer } = useAct();
  const nodes = useLoad(() => ds.table(id, "file_nodes"), [ds, id]);
  const files = useLoad(() => ds.table(id, "wp_files"), [ds, id]);
  const [params, setParams] = useSearchParams();
  const [preview, setPreview] = useState<string | null>(null);
  const sel = params.get("path") ?? undefined;
  const wpById = new Map(model.workpapers.map((w) => [w.id, w]));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <Act command="file.refresh" title="Bring every file up to date" open={open} label="Refresh files…" />
        <Act command="file.folders" title="Create the expected folders" open={open} label="Folders…" />
        <Act command="file.index" title="Rebuild the file index" open={open} label="Index…" />
        <Act command="file.validate" title="Validate the engagement file" open={open} label="Validate…" />
        <Act command="workpaper.open" title="Open a workpaper from a template" open={open} label="Open a workpaper…" />
      </div>
      <LoadState state={nodes}>
        {(ns) => {
          const tree: TreeNode[] = ns.map((n) => ({
            id: n.path, parent: n.parent, label: <span className={n.kind === "phase" ? "font-medium" : ""}>{n.label}</span>, badge: <Badge n={n} />,
            textLabel: `${n.label}${n.totals.workpapers ? `, ${n.totals.reviewed} of ${n.totals.workpapers} workpapers reviewed` : ""}${n.totals.stale ? `, ${n.totals.stale} stale` : ""}${n.problems ? `, ${n.problems} problems` : ""}`,
          }));
          const node = ns.find((n) => n.path === sel);
          const kids = ns.filter((n) => n.parent === (node ? node.path : null));
          return (
            <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <Card title="Folders" className="min-w-0" bodyClass="p-2 max-h-[70vh] overflow-y-auto">
                <Tree nodes={tree} label="Engagement file" selected={sel} onSelect={(p) => { setParams({ path: p }); setPreview(null); }} initiallyOpen={sel ? sel.split("/").map((_, i, a) => a.slice(0, i + 1).join("/")) : []} />
              </Card>
              <div className="min-w-0 space-y-4">
                <Card title={node ? node.label : "Engagement file"} action={node ? <span className="text-xs text-muted">{titleCase(node.kind)}</span> : undefined}>
                  {node && (
                    <>
                      <dl className="mb-3 grid grid-cols-3 gap-2 text-center text-xs sm:grid-cols-6">
                        {([["Items", node.items], ["Workpapers", node.totals.workpapers], ["Rendered", node.totals.rendered], ["Reviewed", node.totals.reviewed], ["Stale", node.totals.stale], ["Open notes", node.totals.openNotes]] as const).map(([k, v]) => (
                          <div key={k} className="rounded border border-line bg-sunken px-1 py-1.5"><dt className="text-muted">{k}</dt><dd className="text-base font-semibold tabular-nums">{v}</dd></div>
                        ))}
                      </dl>
                      {node.totals.workpapers > 0 && <p className="mb-3"><Meter value={node.totals.reviewed} max={node.totals.workpapers} label="Workpapers reviewed in this folder" /></p>}
                      {!node.exists && <p className="mb-2 text-sm text-blocked">The engine reports this folder missing on disk ({plural(node.problems, "problem")}).</p>}
                    </>
                  )}
                  {node?.workpapers.length ? (
                    <ul className="mb-3 divide-y divide-line" aria-label="Workpapers in this folder">
                      {node.workpapers.map((w) => {
                        const full = wpById.get(w.id);
                        return (
                          <li key={w.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                            <Link className="min-w-0 flex-1 hover:underline" to={`/e/${id}/details/wp/${w.id}`}><span className="font-mono">{w.id}</span> {w.title}</Link>
                            {full ? <SignoffCircles signoffs={full.signoffs} scheme={full.signoffScheme} /> : <FlagCircles w={w} />}
                            {w.stale && <StatusPill state="stale" title={full?.stale?.why} />}
                            <StatusPill state={full?.state ?? (w.rendered ? "rendered" : "not_rendered")} label={full?.stateLabel} />
                            {w.openNotes > 0 && <StatusPill state="open" label={`${w.openNotes} open note${w.openNotes === 1 ? "" : "s"}`} />}
                            <Button size="sm" onClick={() => setPreview(w.id)} aria-label={`Preview ${w.id}`} disabled={!w.rendered} title={w.rendered ? undefined : "Not rendered yet"}>Preview</Button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {kids.length === 0 && !node?.workpapers.length ? <p className="text-sm text-muted">{node ? `No workpapers or sub-folders here${node.items ? ` (${plural(node.items, "file")} the engine counts; file names are not published)` : ""}.` : "Choose a folder."}</p> : (
                    <ul className="divide-y divide-line" aria-label="Sub-folders">
                      {kids.map((n) => (
                        <li key={n.path} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                          <span className="w-20 shrink-0 text-xs text-muted">{titleCase(n.kind)}</span>
                          <button type="button" className="min-w-0 flex-1 text-left hover:underline" onClick={() => setParams({ path: n.path })}>{n.label}</button>
                          {n.totals.openNotes > 0 && <StatusPill state="open" label={`${n.totals.openNotes} open`} />}
                          <Badge n={n} />
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
                {preview && <Preview wp={wpById.get(preview) ?? null} wpId={preview} files={files.data} filesError={files.error} onClose={() => setPreview(null)} />}
              </div>
            </div>
          );
        }}
      </LoadState>
      {drawer}
    </div>
  );
}

/** Sign-off facts from the tree when the workpaper row is not loaded: engine booleans only, shown as rung letters. */
function FlagCircles({ w }: { w: FileNode["workpapers"][number] }) {
  const rungs: Array<[string, boolean]> = [["P", w.prepared], ["R", w.reviewed], ["EP", w.partner], ["EQ", w.eqr]];
  return <span className="inline-flex gap-1" role="list" aria-label="Sign-offs">{rungs.map(([r, on]) => <span key={r} role="listitem" aria-label={`${r}: ${on ? "signed" : "not signed"}`}
    className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full border px-1 text-[11px] font-semibold ${on ? "border-done bg-done text-paper" : "border-line text-muted"}`}>{r}</span>)}</span>;
}

/** File preview through a signed URL (private bucket), with a clear fallback when there is none. */
export function Preview({ wp, wpId, files, filesError, onClose }: { wp: Workpaper | null; wpId: string; files: WpFile[] | undefined; filesError?: string; onClose?: () => void }) {
  const { id } = useEngagement();
  const { ds } = useApp();
  const file = (files ?? []).find((f) => f.workpaper === wpId) ?? (wp?.file ? (files ?? []).find((f) => f.path === wp.file) : undefined);
  const url = useLoad(() => (file ? ds.fileUrl(id, file.storagePath) : Promise.resolve(null)), [ds, id, file?.storagePath]);
  return (
    <Card title={`Preview · ${wpId}`} action={onClose ? <Button size="sm" onClick={onClose}>Close preview</Button> : undefined}>
      {filesError ? <ErrorState error={`Rendered files could not be listed: ${filesError}`} />
        : !files || url.loading ? <p className="text-sm text-muted">Asking for a signed link…</p>
        : url.data ? (
          <div className="space-y-2">
            <p className="text-xs text-muted">{file?.path} · rendered {fmtStamp(file?.renderedAt)} · the link expires in five minutes; the local file stays the system of record.</p>
            {/\.pdf$/i.test(file?.path ?? "") ? <iframe title={`Preview of ${wpId}`} src={url.data} className="h-[60vh] w-full rounded border border-line" />
              : <a className="text-brand underline" href={url.data} target="_blank" rel="noreferrer">Open {file?.path.split("/").pop()}</a>}
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-line px-3 py-3 text-sm">
            <p className="font-medium">No preview available.</p>
            <p className="mt-1 text-muted">
              {!file ? "The engine has not uploaded this rendered workpaper to the private bucket (no row in wp_files)."
                : ds.mode === "demo" ? "Demo data has no storage bucket, so there is no signed link to give."
                : "Storage refused a signed link (you may not be on this engagement's team, or the file was purged after retention)."}
            </p>
            {wp?.file && <p className="mt-1 font-mono text-xs text-muted">On the engine's machine: {wp.file}</p>}
          </div>
        )}
    </Card>
  );
}
